import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter, throttleTime } from 'rxjs/operators';

import { AccessGuard } from '@app/access/access-guard';
import { EAccess } from '@app/access/access.model';
import { AuthenticationService } from '@core/services/auth.service';
import { WorkflowRequestService } from './workflow-request.service';

/**
 * Pending approval-request state feeding the approvals surfaces (sidebar badge, dashboard
 * tile). Deliberate sibling of AlertNotificationService — the two feeds stay separate
 * because their audiences, gates (workflowEventsEnabled vs riskAlertsEnabled) and badge
 * counts are independent. Same contract: the SERVER decides visibility via canView; the
 * SPA never infers it.
 */
@Injectable({ providedIn: 'root' })
export class ApprovalNotificationService {
  public readonly pendingCount$ = new BehaviorSubject<number>(0);
  /** True when the current user can see approvals at all (project toggle + server canView). */
  public readonly available$ = new BehaviorSubject<boolean>(false);

  constructor(
    router: Router,
    private auth: AuthenticationService,
    private workflowRequestService: WorkflowRequestService
  ) {
    this.auth.currentUserObs.subscribe(() => this.refresh());
    // Keep the count live as the user moves around, without polling on a timer.
    router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        throttleTime(10000)
      )
      .subscribe(() => this.refresh());
  }

  public refresh(): void {
    const user = this.auth.currentUser as any;
    const permitted = !!user?.workflowEventsEnabled && AccessGuard.hasAccess(EAccess.WorkflowRequest);
    if (!permitted) {
      this.available$.next(false);
      this.pendingCount$.next(0);
      return;
    }

    this.workflowRequestService.getPendingApprovalCount().subscribe({
      next: (res) => {
        // Server-decided visibility: admin tiers always; everyone else only while they are
        // a configured recipient of the session-extension workflow.
        const canView = res?.canView !== false;
        this.available$.next(canView);
        this.pendingCount$.next(canView ? (res?.pendingCount ?? 0) : 0);
      },
      error: () => {
        // Leave the last known state - a transient failure must not flap the surfaces.
      }
    });
  }
}
